/** Per-user color-palette library — Tier 3 Group D Slice 1b.
 *
 *  User palettes are persisted alongside the recent-colors strip
 *  but live in their own namespace + version so a future shape
 *  change to one doesn't ripple into the other. Each palette is a
 *  flat list of `#rrggbb` colors with a display name and an active
 *  cursor that selects which palette the picker currently shows.
 *
 *  Built-in palettes (`packages/.../builtin-palettes.ts`) live in
 *  code, not in this store. The user can duplicate a built-in into
 *  their library to start editing — duplication is what gives
 *  builtins effective edit-after-fork behavior without making the
 *  builtin store mutable.
 *
 *  Storage shape (`v1`):
 *    {
 *      palettes: [{ id, name, colors }],
 *      activePaletteId: string | null,
 *    }
 *
 *  - `activePaletteId` may reference a builtin id (`builtin:<slug>`)
 *    or a user palette id. The picker resolves it at read time.
 *  - User palette ids look like `user:<crockford-base32-ulid>`. A
 *    tiny inline id-maker keeps us out of any new dependency.
 *
 *  Quota / private-mode failures degrade silently via the shared
 *  `safeStorage` shim. */

import { createPersistedNamespace, safeStorage } from './user-prefs-storage';
import { normalizeRecentColor } from './recent-colors-prefs';

type PalettesListener = (state: StoredShape) => void;
const listeners = new Set<PalettesListener>();

/** Subscribe to store mutations. Called whenever any of the
 *  mutating functions in this module (`createPalette`, `rename`,
 *  `delete`, `appendColor`, `removeColor`, `setActive`, `clearAll`)
 *  successfully changes state. Returns an unsubscribe function. */
export function subscribeToUserPalettes(listener: PalettesListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(state: StoredShape): void {
  listeners.forEach((l) => l(state));
}

/** Test-only: drop every registered listener. Components that
 *  failed to unmount cleanly in a prior test would otherwise leak
 *  React state-setter references across tests. */
export function __resetPalettesListenersForTests(): void {
  listeners.clear();
}

export interface UserPalette {
  /** `user:<rand>` — never `builtin:*`. */
  id: string;
  /** Display name. Empty / whitespace-only names are rejected at
   *  create time (rename allows '' transiently in the form, but
   *  the commit re-validates). */
  name: string;
  /** Ordered list of hex colors. Caps at MAX_PALETTE_COLORS so
   *  a runaway "add current color" sequence can't blow out the
   *  storage budget. */
  colors: string[];
}

interface StoredShape {
  palettes: UserPalette[];
  activePaletteId: string | null;
}

const MAX_PALETTES = 32;
const MAX_PALETTE_COLORS = 256;

const namespace = createPersistedNamespace<StoredShape>({
  storageKey: 'image-editor:user-palettes:v1',
  defaultValue: { palettes: [], activePaletteId: null },
  parse: (raw): StoredShape => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return { palettes: [], activePaletteId: null };
    }
    const candidate = raw as Record<string, unknown>;
    const palettesRaw = Array.isArray(candidate.palettes) ? candidate.palettes : [];
    const activeRaw =
      typeof candidate.activePaletteId === 'string' ? candidate.activePaletteId : null;
    const palettes = palettesRaw
      .map((p): UserPalette | null => {
        if (typeof p !== 'object' || p === null) return null;
        const pp = p as Record<string, unknown>;
        if (typeof pp.id !== 'string' || !pp.id.startsWith('user:')) return null;
        if (typeof pp.name !== 'string') return null;
        if (!Array.isArray(pp.colors)) return null;
        const colors = pp.colors
          .filter((c): c is string => typeof c === 'string')
          .map((c) => normalizeRecentColor(c))
          .filter((c): c is string => c !== null)
          .slice(0, MAX_PALETTE_COLORS);
        return { id: pp.id, name: pp.name, colors };
      })
      .filter((p): p is UserPalette => p !== null)
      .slice(0, MAX_PALETTES);
    return { palettes, activePaletteId: activeRaw };
  },
});

function generateId(): string {
  // Crockford base32 alphabet, 10 random chars — collision odds at
  // 32 entries are vanishing (~10⁻¹³). Avoids adding `uuid` or
  // `nanoid` as a dep for what's a single helper.
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let out = 'user:';
  for (let i = 0; i < 10; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/** In-memory source of truth. Hydrated from storage on module
 *  init; every mutator updates this AND writes through to storage.
 *  Read paths consult the in-memory snapshot only, which sidesteps
 *  test environments that mock `localStorage` with stubs that drop
 *  writes silently — see `client/src/test/setup.ts`. */
let inMemoryState: StoredShape = namespace.load();

/** Load the full store. Returns the live in-memory snapshot
 *  reference; callers should not mutate the returned object
 *  directly (all mutators below produce a new state object). */
export function loadUserPalettes(_storage: Storage = safeStorage()): StoredShape {
  return inMemoryState;
}

/** Persist the full store and notify subscribers. All mutators
 *  funnel through here so a single emit covers both the storage
 *  write and the in-memory notification. */
function save(state: StoredShape, storage: Storage = safeStorage()): void {
  inMemoryState = state;
  namespace.save(state, storage);
  emit(state);
}

/** Test-only: reset the in-memory snapshot back to the empty
 *  default. Used by `__resetPalettesForTests` so a previous test's
 *  state doesn't leak into the next test's first render. */
export function __resetPalettesInMemoryForTests(): void {
  inMemoryState = { palettes: [], activePaletteId: null };
}

/** Test-only: re-parse the storage payload into the in-memory
 *  snapshot. Lets the parse-validation tests assert behavior the
 *  module-init load runs exactly once — they put a payload into
 *  a fake storage, hydrate, and then read `loadUserPalettes()`. */
export function __reloadPalettesFromStorageForTests(storage: Storage): void {
  inMemoryState = namespace.load(storage);
}

/** Create a new empty palette. Returns the new state. The new
 *  palette becomes the active palette. */
export function createPalette(
  name: string,
  colors: string[] = [],
  storage: Storage = safeStorage(),
): StoredShape {
  const trimmed = name.trim();
  const state = inMemoryState;
  if (state.palettes.length >= MAX_PALETTES) return state;
  const normalizedColors = colors
    .map((c) => normalizeRecentColor(c))
    .filter((c): c is string => c !== null)
    .slice(0, MAX_PALETTE_COLORS);
  const newPalette: UserPalette = {
    id: generateId(),
    name: trimmed || 'Untitled palette',
    colors: normalizedColors,
  };
  const next: StoredShape = {
    palettes: [...state.palettes, newPalette],
    activePaletteId: newPalette.id,
  };
  save(next, storage);
  return next;
}

/** Rename a palette. No-op if the id is unknown or the new name
 *  is whitespace-only. */
export function renamePalette(
  id: string,
  name: string,
  storage: Storage = safeStorage(),
): StoredShape {
  const trimmed = name.trim();
  if (!trimmed) return inMemoryState;
  const state = inMemoryState;
  const next: StoredShape = {
    ...state,
    palettes: state.palettes.map((p) => (p.id === id ? { ...p, name: trimmed } : p)),
  };
  save(next, storage);
  return next;
}

/** Delete a palette. If the deleted palette was active the cursor
 *  resets to null (the picker falls back to the first built-in). */
export function deletePalette(id: string, storage: Storage = safeStorage()): StoredShape {
  const state = inMemoryState;
  const next: StoredShape = {
    palettes: state.palettes.filter((p) => p.id !== id),
    activePaletteId: state.activePaletteId === id ? null : state.activePaletteId,
  };
  save(next, storage);
  return next;
}

/** Append a color to a palette. Dedupes — re-adding an existing
 *  color is a no-op (no promotion to head; palettes are
 *  user-ordered, not LRU). Built-in ids are rejected (kind check
 *  is at the caller layer — this just bails on unknown user ids). */
export function appendColorToPalette(
  id: string,
  color: string,
  storage: Storage = safeStorage(),
): StoredShape {
  const normalized = normalizeRecentColor(color);
  if (!normalized) return inMemoryState;
  const state = inMemoryState;
  const palette = state.palettes.find((p) => p.id === id);
  if (!palette) return state;
  if (palette.colors.includes(normalized)) return state;
  if (palette.colors.length >= MAX_PALETTE_COLORS) return state;
  const next: StoredShape = {
    ...state,
    palettes: state.palettes.map((p) =>
      p.id === id ? { ...p, colors: [...p.colors, normalized] } : p,
    ),
  };
  save(next, storage);
  return next;
}

/** Remove a color from a palette by exact-hex match. */
export function removeColorFromPalette(
  id: string,
  color: string,
  storage: Storage = safeStorage(),
): StoredShape {
  const normalized = normalizeRecentColor(color);
  if (!normalized) return inMemoryState;
  const state = inMemoryState;
  const next: StoredShape = {
    ...state,
    palettes: state.palettes.map((p) =>
      p.id === id ? { ...p, colors: p.colors.filter((c) => c !== normalized) } : p,
    ),
  };
  save(next, storage);
  return next;
}

/** Persist the user's currently-active palette cursor. `null`
 *  surfaces the first built-in. */
export function setActivePaletteId(
  id: string | null,
  storage: Storage = safeStorage(),
): StoredShape {
  const state = inMemoryState;
  const next = { ...state, activePaletteId: id };
  save(next, storage);
  return next;
}

/** Wipe all user palettes (and the active cursor). Wired to the
 *  manage-palettes dialog's "Reset" affordance. */
export function clearAllUserPalettes(storage: Storage = safeStorage()): void {
  inMemoryState = { palettes: [], activePaletteId: null };
  namespace.clear(storage);
  emit({ palettes: [], activePaletteId: null });
}

export const PALETTE_LIMITS = {
  maxPalettes: MAX_PALETTES,
  maxColorsPerPalette: MAX_PALETTE_COLORS,
};
